import type { ShipClassId } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { hullShare, isArmed, leadShip, type FleetState, type GameState } from '@sd/sim';
import { escapeHtml, fleetName } from './format';
import { icon } from './icons';

/**
 * The units bar (#154, the author's request of 28.10.2026): every ship of the
 * player as a card at the bottom of the map. A click selects it, a double click
 * or its digit pressed twice flies the camera to it; Shift adds it to those
 * picked. Idle ones stand out: they wait for an order.
 */
export interface UnitCard {
  readonly id: number;
  readonly name: string;
  readonly lead: ShipClassId;
  readonly armed: boolean;
  readonly idle: boolean;
  /** What it does, in two or three words. */
  readonly doing: string;
  /** Hull left, per cent. */
  readonly hull: number;
}

export type UnitFilter = 'all' | 'idle' | 'armed';
const FILTERS: readonly UnitFilter[] = ['all', 'idle', 'armed'];

/** The player's ships, oldest first: a card keeps its place as others come and go. */
export function unitCards(state: GameState, me: string, locale: Locale): UnitCard[] {
  return state.fleets
    .filter((f) => f.owner === me)
    .sort((a, b) => leadShip(a).id - leadShip(b).id)
    .map((f) => card(state, f, locale));
}

function card(state: GameState, f: FleetState, locale: Locale): UnitCard {
  const caravan = state.contracts.some((c) => c.caravan?.fleetId === f.id);
  const raiding = state.raids.some((r) => r.fleetId === f.id);
  const doing = f.retreating
    ? t(locale, 'units.retreating')
    : caravan
      ? t(locale, 'units.caravan')
      : raiding
        ? t(locale, 'units.raiding')
        : f.scan
          ? t(locale, 'units.scanning')
          : f.path.length
            ? t(locale, 'units.moving', { hexes: f.path.length })
            : t(locale, 'units.idle');
  return {
    id: f.id,
    name: fleetName(locale, f),
    lead: leadShip(f).cls,
    armed: isArmed(f),
    idle: !f.retreating && !caravan && !raiding && !f.scan && f.path.length === 0,
    doing,
    hull: Math.round(hullShare(f) * 100),
  };
}

export const keep = (filter: UnitFilter) => (c: UnitCard) =>
  filter === 'all' || (filter === 'idle' ? c.idle : c.armed);

/** A silhouette per class: hulls that fight are pointed, the rest are blunt. */
const SILHOUETTE: Readonly<Record<ShipClassId, string>> = {
  explorer: '<path d="M12 3l6 9-6 9-6-9z"/><circle cx="12" cy="12" r="2.5"/>',
  transport: '<path d="M4 8h16v10H4zM8 8V5h8v3"/>',
  corvette: '<path d="M12 4l6 16-6-4-6 4z"/>',
  frigate: '<path d="M12 3l7 17-7-4-7 4zM12 8v8"/>',
  cruiser: '<path d="M12 2l9 19-9-5-9 5zM7 14h10"/>',
  tradeLiner: '<rect x="3" y="7" width="18" height="10" rx="2"/><path d="M7 12h10"/>',
  raider: '<path d="M12 2l3 12-3 8-3-8zM6 14h12"/>',
  lander: '<path d="M12 3l6 11H6zM8 14l-3 6M16 14l3 6"/>',
};

export const shipIcon = (cls: ShipClassId, size = 18): string =>
  `<svg class="sd-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">${SILHOUETTE[cls]}</svg>`;

export interface UnitsBarHandlers {
  /** Pick a unit; `add` keeps the ones already picked. */
  select(fleetId: number, add: boolean): void;
  /** Fly the camera to it. */
  fly(fleetId: number): void;
}

/** How long a second press of the same digit counts as «fly there», ms. */
const TWICE_MS = 450;
/** Cards are rebuilt at most this often: the bar needs no more. */
const REDRAW_MS = 400;

export class UnitsBar {
  private readonly el: HTMLElement;
  private filter: UnitFilter = 'all';
  private cards: UnitCard[] = [];
  private selected = new Set<number>();
  private hit: ReadonlySet<number> = new Set();
  private last = '';
  private lastDraw = 0;
  private lastKey: { code: string; at: number } | null = null;

  constructor(
    private readonly parent: HTMLElement,
    private readonly locale: Locale,
    private readonly me: string,
    private readonly on: UnitsBarHandlers,
  ) {
    this.el = document.createElement('nav');
    this.el.className = 'sd-units';
    this.el.setAttribute('aria-label', t(locale, 'units.title'));
    parent.append(this.el);
    // The selection card and the hints stand above the bar: they need its height.
    new ResizeObserver(() => {
      parent.style.setProperty('--units-h', `${this.el.hidden ? 0 : this.el.offsetHeight}px`);
    }).observe(this.el);
    this.el.addEventListener('click', (e) => this.click(e));
    this.el.addEventListener('dblclick', (e) => {
      const id = (e.target as HTMLElement).closest<HTMLElement>('[data-unit]')?.dataset.unit;
      if (id) this.on.fly(Number(id));
    });
  }

  update(state: GameState, force = false) {
    const now = performance.now();
    if (!force && now - this.lastDraw < REDRAW_MS) return;
    this.lastDraw = now;
    this.cards = unitCards(state, this.me, this.locale);
    this.draw();
  }

  /** The fleets picked on the map, lit on the bar. */
  setSelected(ids: readonly number[]) {
    const next = new Set(ids);
    if (next.size === this.selected.size && [...next].every((id) => this.selected.has(id))) return;
    this.selected = next;
    this.draw();
  }

  /** Fleets hit lately (#156): a red mark on their cards. */
  setHit(ids: ReadonlySet<number>) {
    if (ids.size === this.hit.size && [...ids].every((id) => this.hit.has(id))) return;
    this.hit = ids;
    this.draw();
  }

  /** Digits 1–9 pick the cards shown; the same digit twice flies there. True when used. */
  handleKey(e: KeyboardEvent): boolean {
    const digit = /^Digit([1-9])$/.exec(e.code);
    if (!digit || e.ctrlKey || e.metaKey || e.altKey) return false;
    const unit = this.shown()[Number(digit[1]) - 1];
    if (!unit) return false;
    const now = performance.now();
    const twice = this.lastKey?.code === e.code && now - this.lastKey.at < TWICE_MS;
    this.lastKey = { code: e.code, at: now };
    this.on.select(unit.id, e.shiftKey);
    if (twice) this.on.fly(unit.id);
    return true;
  }

  private shown(): UnitCard[] {
    return this.cards.filter(keep(this.filter));
  }

  private click(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const filter = target.closest<HTMLElement>('[data-filter]')?.dataset.filter;
    if (filter) {
      this.filter = filter as UnitFilter;
      return this.draw();
    }
    const id = target.closest<HTMLElement>('[data-unit]')?.dataset.unit;
    if (id) this.on.select(Number(id), e.shiftKey);
  }

  private draw() {
    const { locale } = this;
    const shown = this.shown();
    const count = (f: UnitFilter) => this.cards.filter(keep(f)).length;
    const html = this.cards.length
      ? `<div class="sd-units__filters" role="group" aria-label="${t(locale, 'units.filter')}">${FILTERS.map(
          (f) =>
            `<button type="button" class="sd-units__filter" data-filter="${f}" aria-pressed="${f === this.filter}">${t(locale, `units.filter.${f}`)} <b>${count(f)}</b></button>`,
        ).join(
          '',
        )}<span class="sd-units__hint" tabindex="0" role="note" title="${t(locale, 'units.hint')}" aria-label="${t(locale, 'units.hint')}">${icon('info', 15)}</span></div>
        <div class="sd-units__cards" role="list">${shown
          .map((c, i) => {
            const cls = [
              'sd-panel',
              'sd-unit',
              c.idle ? 'sd-unit--idle' : '',
              this.selected.has(c.id) ? 'sd-unit--picked' : '',
              this.hit.has(c.id) ? 'sd-unit--hit' : '',
            ].join(' ');
            const hit = this.hit.has(c.id);
            return `<button type="button" role="listitem" class="${cls}" data-unit="${c.id}" title="${escapeHtml(`${c.name} · ${hit ? t(locale, 'units.hit') : c.doing}`)}">
              ${hit ? '<i class="sd-hit" aria-hidden="true">!</i>' : ''}
              ${i < 9 ? `<span class="sd-unit__key">${i + 1}</span>` : ''}
              <span class="sd-unit__name">${shipIcon(c.lead)}<b>${escapeHtml(c.name)}</b></span>
              <span class="sd-unit__doing">${escapeHtml(c.doing)}</span>
              <span class="sd-unit__hull ${c.hull < 50 ? 'sd-unit__hull--low' : ''}"><span style="width:${c.hull}%"></span></span>
            </button>`;
          })
          .join(
            '',
          )}${shown.length ? '' : `<span class="sd-label sd-units__none">${t(locale, 'units.none')}</span>`}</div>`
      : '';
    if (html === this.last) return;
    this.last = html;
    this.el.innerHTML = html;
    this.el.hidden = !this.cards.length;
    this.parent.style.setProperty('--units-h', `${this.el.hidden ? 0 : this.el.offsetHeight}px`);
  }

  dispose() {
    this.el.remove();
    this.parent.style.removeProperty('--units-h');
  }
}
